import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import type { Locale } from "../../shared/dto";
import { staffMutation } from "./model";
import { adminCallPreview, buildWeeklyCallStarts } from "./admin-call-time";
import type { AdminCallStartParts } from "./admin-call-time";
import { AdminGlobalUnlock } from "./AdminGlobalUnlock";
import { clockFields, intlLanguage } from "../date-format";

export interface AdminCall {
  id: string;
  kind: "onboarding" | "marketing";
  locale: Locale;
  startsAt: string;
  zoomUrl: string;
  recordingUrl: string | null;
  seriesId: string | null;
  intervalWeeks: number | null;
  seriesOccurrenceCount: number | null;
  capacity: number;
  cancelledAt: string | null;
  codeId: string | null;
  codeActive: number | null;
  codeNotBefore: string | null;
  codeExpiresAt: string | null;
  codeRedemptionCount: number;
  codeLastRedeemedAt: string | null;
  enrolledCount: number;
  attendedCount: number;
  watchedCount: number;
}

export interface CreatedAdminCalls {
  seriesId: string | null;
  occurrences: Array<{
    occurrenceId: string;
    startsAt: string;
    zoomUrl: string;
    codeId: string;
    code: string;
  }>;
}

interface CommonProps {
  t: (key: string) => string;
  locale: Locale;
}

export interface AdminCallClient {
  id: string;
  name: string;
  email: string;
}

export interface AdminStableCallLinks {
  onboarding: string | null;
  marketing: string | null;
}

export const PAST_CALL_BATCH_SIZE = 12;

type AdminDateTimeParts = AdminCallStartParts;

export function AdminCalls(props: CommonProps & { csrfToken: string; clients: AdminCallClient[] }) {
  const [kind, setKind] = useState<AdminCall["kind"]>("onboarding");
  const [locale, setLocale] = useState<Locale>(props.locale);
  const [dateTime, setDateTime] = useState<AdminDateTimeParts>({
    day: "", month: "", year: "", hour: "", minute: ""
  });
  const [zoomUrl, setZoomUrl] = useState("");
  const [capacity, setCapacity] = useState(20);
  const [repeat, setRepeat] = useState(false);
  const [intervalWeeks, setIntervalWeeks] = useState(1);
  const [occurrenceCount, setOccurrenceCount] = useState(8);
  const [calls, setCalls] = useState<AdminCall[]>([]);
  const [listState, setListState] = useState<"loading" | "ready" | "error">("loading");
  const [formState, setFormState] = useState<"idle" | "saving" | "error">("idle");
  const [actionState, setActionState] = useState<"idle" | "busy" | "error" | "done">("idle");
  const [actionMessage, setActionMessage] = useState("staffConsole.adminCalls.actionDone");
  const [created, setCreated] = useState<CreatedAdminCalls | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<"upcoming" | "past">("upcoming");
  const [historyQuery, setHistoryQuery] = useState("");
  const [historyKind, setHistoryKind] = useState<"all" | AdminCall["kind"]>("all");
  const [historyPeriod, setHistoryPeriod] = useState<"30" | "90" | "all">("30");
  const [pastVisibleCount, setPastVisibleCount] = useState(PAST_CALL_BATCH_SIZE);

  const load = useCallback(async () => {
    setListState("loading");
    try {
      const response = await fetch("/api/admin/calls", { credentials: "include" });
      const body = await response.json() as { ok?: boolean; calls?: AdminCall[] };
      if (!response.ok || !body.ok || !Array.isArray(body.calls)) throw new Error("calls_load_failed");
      setCalls(body.calls.map(normalizeCall));
      setListState("ready");
    } catch {
      setListState("error");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!created) return;
    const target = document.querySelector<HTMLElement>(".admin-call-confirmation");
    if (!target) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    target.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "nearest" });
  }, [created]);

  // Jeden stav pro náhled i pro zámek tlačítka: co panel napíše, to tlačítko
  // dovolí. Odmítnutí nese svůj důvod (viz admin-call-time.ts).
  const preview = useMemo(
    () => adminCallPreview(dateTime, { repeat, intervalWeeks, occurrenceCount, now: Date.now(), zoomUrl }),
    [dateTime, repeat, intervalWeeks, occurrenceCount, zoomUrl]
  );
  const previewStarts = preview.status === "ready" ? preview.starts : [];

  const filteredCalls = useMemo(() => {
    const now = Date.now();
    if (tab === "upcoming") {
      return calls
        .filter((call) => Date.parse(call.startsAt) >= now)
        .sort((left, right) => Date.parse(left.startsAt) - Date.parse(right.startsAt));
    }
    const needle = historyQuery.trim().toLocaleLowerCase(props.locale);
    const cutoff = historyPeriod === "all"
      ? Number.NEGATIVE_INFINITY
      : now - Number(historyPeriod) * 86_400_000;
    return calls
      .filter((call) => Date.parse(call.startsAt) < now && Date.parse(call.startsAt) >= cutoff)
      .filter((call) => historyKind === "all" || call.kind === historyKind)
      .filter((call) => !needle || `${formatCallDate(call.startsAt, props.locale)} ${props.t(`staffConsole.callKindValue.${call.kind}`)}`
        .toLocaleLowerCase(props.locale).includes(needle))
      .sort((left, right) => Date.parse(right.startsAt) - Date.parse(left.startsAt));
  }, [calls, historyKind, historyPeriod, historyQuery, props.locale, props.t, tab]);
  const visibleCalls = tab === "past" ? filteredCalls.slice(0, pastVisibleCount) : filteredCalls;

  useEffect(() => {
    setPastVisibleCount(PAST_CALL_BATCH_SIZE);
    setSelectedId(null);
  }, [historyKind, historyPeriod, historyQuery]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!previewStarts.length) return;
    setFormState("saving");
    setCreated(null);
    const response = await staffMutation("/api/admin/calls", {
      method: "POST",
      csrfToken: props.csrfToken,
      body: {
        kind,
        locale,
        startsAt: previewStarts,
        ...(repeat ? { intervalWeeks } : {}),
        zoomUrl,
        recordingUrl: null,
        capacity
      }
    });
    if (!response.ok) {
      setFormState("error");
      return;
    }
    const result = await response.json() as CreatedAdminCalls;
    setCreated(result);
    setFormState("idle");
    setTab("upcoming");
    setSelectedId(result.occurrences[0]?.occurrenceId ?? null);
    await load();
  }

  async function cancel(call: AdminCall) {
    const message = props.t("staffConsole.adminCalls.confirm.cancelOne")
      .replace("{date}", formatCallDate(call.startsAt, props.locale))
      .replace("{count}", String(call.enrolledCount));
    if (!window.confirm(message)) return;
    await mutate(`/api/admin/calls/${call.id}/cancel`, {}, "staffConsole.adminCalls.feedback.callCancelled");
  }

  async function cancelFuture(call: AdminCall) {
    if (!call.seriesId || !window.confirm(props.t("staffConsole.adminCalls.confirm.cancelFuture"))) return;
    await mutate(
      `/api/admin/call-series/${call.seriesId}/cancel-future`,
      { fromOccurrenceId: call.id },
      "staffConsole.adminCalls.feedback.futureCancelled"
    );
  }

  async function extend(call: AdminCall) {
    if (!call.seriesId || !call.intervalWeeks) return;
    const seriesCalls = calls.filter((candidate) => candidate.seriesId === call.seriesId)
      .sort((left, right) => Date.parse(left.startsAt) - Date.parse(right.startsAt));
    const last = seriesCalls.at(-1);
    if (!last) return;
    const count = Math.min(8, 104 - (call.seriesOccurrenceCount ?? seriesCalls.length));
    if (count < 1) return;
    const next = new Date(last.startsAt);
    next.setDate(next.getDate() + call.intervalWeeks * 7);
    const starts = buildWeeklyCallStarts(toLocalDateTimeInput(next), call.intervalWeeks, count);
    setActionState("busy");
    const response = await staffMutation(`/api/admin/call-series/${call.seriesId}/extend`, {
      method: "POST", csrfToken: props.csrfToken, body: { startsAt: starts }
    });
    if (!response.ok) {
      setActionState("error");
      return;
    }
    const result = await response.json() as CreatedAdminCalls;
    setCreated(result);
    setSelectedId(result.occurrences[0]?.occurrenceId ?? call.id);
    setActionMessage("staffConsole.adminCalls.feedback.seriesExtended");
    setActionState("done");
    await load();
  }

  async function mutate(path: string, body: unknown, successMessage: string) {
    setActionState("busy");
    const response = await staffMutation(path, { method: "POST", csrfToken: props.csrfToken, body });
    if (!response.ok) {
      setActionState("error");
      return;
    }
    setActionMessage(successMessage);
    setActionState("done");
    await load();
  }

  async function issueCode(call: AdminCall, renew: boolean) {
    const notBefore = new Date(Date.parse(call.startsAt) - 30 * 60 * 1000).toISOString();
    const expiresAt = new Date(Date.parse(call.startsAt) + 14 * 86_400_000).toISOString();
    const path = renew && call.codeId
      ? `/api/admin/codes/${call.codeId}/renew`
      : `/api/admin/calls/${call.id}/code`;
    setActionState("busy");
    const response = await staffMutation(path, {
      method: "POST", csrfToken: props.csrfToken, body: { notBefore, expiresAt }
    });
    if (!response.ok) {
      setActionState("error");
      return;
    }
    const result = await response.json() as { codeId: string; code: string };
    setCreated({
      seriesId: call.seriesId,
      occurrences: [{
        occurrenceId: call.id,
        startsAt: call.startsAt,
        zoomUrl: call.zoomUrl,
        codeId: result.codeId,
        code: result.code
      }]
    });
    setSelectedId(call.id);
    setActionMessage(renew
      ? "staffConsole.adminCalls.feedback.codeRenewed"
      : "staffConsole.adminCalls.feedback.codeCreated");
    setActionState("done");
    await load();
  }

  function openCall(occurrenceId: string) {
    setSelectedId(occurrenceId);
    setTab("upcoming");
    window.requestAnimationFrame(() => {
      const target = document.querySelector<HTMLElement>(".admin-call-overview");
      if (!target) return;
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      target.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
    });
  }

  return (
    // F138d: the panel's summary carries the name and the sentence. The
    // timezone chip is a fact about the times below it, so it stays.
    <section className="admin-calls">
      <header className="admin-calls-head">
        <span className="status-chip">{props.t("staffConsole.adminCalls.timezone.browser")}</span>
      </header>

      <div className="admin-calls-create-grid">
        <form className="admin-call-form" onSubmit={submit}>
          <div className="admin-call-field-grid">
            <label>
              <span>{props.t("staffConsole.callKind")}</span>
              <select value={kind} onChange={(event) => setKind(event.target.value as AdminCall["kind"])}>
                <option value="onboarding">{props.t("staffConsole.callKindValue.onboarding")}</option>
                <option value="marketing">{props.t("staffConsole.callKindValue.marketing")}</option>
              </select>
            </label>
            <label>
              <span>{props.t("staffConsole.callLocale")}</span>
              <select value={locale} onChange={(event) => setLocale(event.target.value as Locale)}>
                <option value="cs">{props.t("common.languageCs")}</option>
                <option value="en">{props.t("common.languageEn")}</option>
              </select>
            </label>
            <AdminDateTimeField value={dateTime} onChange={setDateTime} t={props.t} />
            <label>
              <span>{props.t("staffConsole.adminCalls.capacity")}</span>
              <input type="number" min={1} max={500} value={capacity}
                onChange={(event) => setCapacity(Number(event.target.value))} required />
            </label>
          </div>
          <label>
            <span>{props.t("staffConsole.zoomUrl")}</span>
            <input type="url" placeholder="https://zoom.us/" value={zoomUrl}
              onChange={(event) => setZoomUrl(event.target.value)} required />
          </label>
          <fieldset className="admin-call-repeat">
            <legend>{props.t("staffConsole.adminCalls.repeat.label")}</legend>
            <label className="checkbox-row">
              <input type="checkbox" checked={repeat} onChange={(event) => setRepeat(event.target.checked)} />
              <span>{props.t("staffConsole.adminCalls.repeat.weekly")}</span>
            </label>
            {repeat && (
              <div className="admin-call-field-grid repeat-fields">
                <label>
                  <span>{props.t("staffConsole.adminCalls.repeat.interval")}</span>
                  <input type="number" min={1} max={8} value={intervalWeeks}
                    onChange={(event) => setIntervalWeeks(Number(event.target.value))} />
                </label>
                <label>
                  <span>{props.t("staffConsole.adminCalls.repeat.count")}</span>
                  <input type="number" min={2} max={16} value={occurrenceCount}
                    onChange={(event) => setOccurrenceCount(Number(event.target.value))} />
                </label>
              </div>
            )}
          </fieldset>
          <button className="primary-button" type="submit" disabled={formState === "saving" || !previewStarts.length}>
            {props.t(formState === "saving" ? "staffConsole.adminCalls.creating" : "staffConsole.adminCalls.create")}
          </button>
          <div className="admin-call-action-feedback" aria-live="polite">
            {formState === "error" && <p className="error-message" role="alert">{props.t("staffConsole.adminCalls.error.create")}</p>}
          </div>
        </form>

        <aside className="admin-call-preview" aria-live="polite">
          <p className="panel-label">{props.t("staffConsole.adminCalls.preview.kicker")}</p>
          <h4>{props.t("staffConsole.adminCalls.preview.title")}</h4>
          {preview.status === "incomplete" && <p className="staff-empty">{props.t("staffConsole.adminCalls.preview.empty")}</p>}
          {preview.status !== "incomplete" && preview.status !== "ready" && (
            <p className="admin-call-preview-reason" role="status">
              {props.t(`staffConsole.adminCalls.preview.reason.${preview.status}`)}
            </p>
          )}
          {preview.status === "past" && (
            <button className="secondary-button compact-button" type="button"
              onClick={() => setDateTime(preview.suggestion)}>
              {props.t("staffConsole.adminCalls.preview.useNextHour")}
            </button>
          )}
          {previewStarts.length > 0 && (
            <ol>
              {previewStarts.map((value) => <li key={value}><time dateTime={value}>{formatCallDate(value, props.locale)}</time></li>)}
            </ol>
          )}
          {previewStarts.length > 1 && <p>{props.t("staffConsole.adminCalls.preview.seriesNote")}</p>}
        </aside>
      </div>

      {created && <AdminCallConfirmation result={created} locale={props.locale} t={props.t} onOpen={openCall} />}

      <section className="admin-call-overview" aria-labelledby="admin-call-overview-title">
        <div className="admin-call-overview-head">
          <div>
            <p className="panel-label">{props.t("staffConsole.adminCalls.overview.kicker")}</p>
            <h4 id="admin-call-overview-title">{props.t("staffConsole.adminCalls.overview.title")}</h4>
          </div>
          <div className="admin-call-tabs" role="group" aria-label={props.t("staffConsole.adminCalls.overview.title")}>
            {(["upcoming", "past"] as const).map((value) => (
              <button key={value} type="button" aria-pressed={tab === value}
                className={tab === value ? "active" : ""} onClick={() => setTab(value)}>
                {props.t(`staffConsole.adminCalls.tabs.${value}`)}
              </button>
            ))}
          </div>
        </div>
        <AdminCallActionFeedback state={actionState} messageKey={actionMessage} t={props.t} />
        {tab === "past" && (
          <div className="admin-call-history-controls">
            <label>
              <span>{props.t("staffConsole.adminCalls.history.search")}</span>
              <input type="search" value={historyQuery} onChange={(event) => setHistoryQuery(event.target.value)} />
            </label>
            <label>
              <span>{props.t("staffConsole.adminCalls.history.kind")}</span>
              <select value={historyKind} onChange={(event) => setHistoryKind(event.target.value as typeof historyKind)}>
                <option value="all">{props.t("staffConsole.adminCalls.history.allKinds")}</option>
                <option value="onboarding">{props.t("staffConsole.callKindValue.onboarding")}</option>
                <option value="marketing">{props.t("staffConsole.callKindValue.marketing")}</option>
              </select>
            </label>
            <label>
              <span>{props.t("staffConsole.adminCalls.history.period")}</span>
              <select value={historyPeriod} onChange={(event) => setHistoryPeriod(event.target.value as typeof historyPeriod)}>
                <option value="30">{props.t("staffConsole.adminCalls.history.last30")}</option>
                <option value="90">{props.t("staffConsole.adminCalls.history.last90")}</option>
                <option value="all">{props.t("staffConsole.adminCalls.history.allTime")}</option>
              </select>
            </label>
          </div>
        )}
        {listState === "loading" && <p>{props.t("common.loading")}</p>}
        {listState === "error" && (
          <div className="admin-call-load-error" role="alert">
            <p className="error-message">{props.t("staffConsole.adminCalls.error.load")}</p>
            <button className="secondary-button" type="button" onClick={() => void load()}>{props.t("common.retry")}</button>
          </div>
        )}
        {listState === "ready" && !visibleCalls.length && <p className="staff-empty">{props.t(`staffConsole.adminCalls.empty.${tab}`)}</p>}
        {listState === "ready" && visibleCalls.length > 0 && (
          <AdminCallRows calls={visibleCalls} selectedId={selectedId} locale={props.locale} t={props.t}
            busy={actionState === "busy"} onSelect={setSelectedId} onCancel={(call) => void cancel(call)}
            onCancelFuture={(call) => void cancelFuture(call)} onExtend={(call) => void extend(call)}
            onRevokeCode={(call) => call.codeId && void mutate(
              `/api/admin/codes/${call.codeId}/revoke`, {}, "staffConsole.adminCalls.feedback.codeRevoked"
            )}
            onRenewCode={(call) => void issueCode(call, Boolean(call.codeId))} />
        )}
        {listState === "ready" && tab === "past" && visibleCalls.length < filteredCalls.length && (
          <button className="secondary-button admin-call-load-more" type="button"
            onClick={() => setPastVisibleCount((count) => count + PAST_CALL_BATCH_SIZE)}>
            {props.t("staffConsole.adminCalls.history.loadMore")}
          </button>
        )}
      </section>

      <AdminGlobalUnlock t={props.t} locale={props.locale} csrfToken={props.csrfToken} />
      <AdminUnlockLinks t={props.t} clients={props.clients} csrfToken={props.csrfToken} />
    </section>
  );
}

function AdminUnlockLinks(props: { t: (key: string) => string; clients: AdminCallClient[]; csrfToken: string }) {
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(props.clients[0]?.id ?? "");
  const [links, setLinks] = useState<AdminStableCallLinks | null>(null);
  const [state, setState] = useState<
    "idle" | "loading" | "ready" | "saving" | "ensured" | "copied" |
    "load_error" | "save_error" | "copy_error"
  >("idle");
  const filtered = useMemo(() => props.clients.filter((client) =>
    `${client.name} ${client.email}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())
  ), [props.clients, query]);
  const nextSelectedId = selectAdminCallClientId(props.clients, query, selectedId);
  const selected = props.clients.find((client) => client.id === selectedId) ?? null;

  const loadLinks = useCallback(async (clientId: string, signal: AbortSignal) => {
    if (!clientId) return;
    setState("loading");
    try {
      const response = await fetch(`/api/admin/clients/${clientId}/call-links`, { credentials: "include", signal });
      const body = await response.json() as { ok?: boolean; links?: AdminStableCallLinks };
      if (!response.ok || !body.ok || !body.links) throw new Error("call_links_load_failed");
      setLinks(body.links);
      setState("ready");
    } catch {
      if (signal.aborted) return;
      setState("load_error");
    }
  }, []);

  useEffect(() => {
    if (nextSelectedId === selectedId) return;
    setSelectedId(nextSelectedId);
    setLinks(null);
    if (!nextSelectedId) setState("idle");
  }, [nextSelectedId, selectedId]);
  useEffect(() => {
    if (!selectedId) return;
    const controller = new AbortController();
    void loadLinks(selectedId, controller.signal);
    return () => controller.abort();
  }, [loadLinks, selectedId]);

  async function ensureLinks() {
    if (!selectedId) return;
    setState("saving");
    const response = await staffMutation(`/api/admin/clients/${selectedId}/call-links/ensure`, {
      method: "POST", csrfToken: props.csrfToken, body: {}
    });
    if (!response.ok) {
      setState("save_error");
      return;
    }
    const body = await response.json() as { links: AdminStableCallLinks };
    setLinks(body.links);
    setState("ensured");
  }

  async function copyLink(path: string) {
    try {
      await navigator.clipboard.writeText(new URL(path, window.location.origin).toString());
      setState("copied");
    } catch {
      setState("copy_error");
    }
  }

  return (
    <section className="admin-unlock-links" aria-labelledby="admin-unlock-links-title">
      <div className="admin-call-overview-head">
        <div>
          <p className="panel-label">{props.t("staffConsole.adminCalls.links.kicker")}</p>
          <h4 id="admin-unlock-links-title">{props.t("staffConsole.adminCalls.links.title")}</h4>
          <p>{props.t("staffConsole.adminCalls.links.hint")}</p>
        </div>
      </div>
      <label className="admin-client-search">
        <span>{props.t("staffConsole.adminCalls.links.search")}</span>
        <input type="search" value={query} disabled={state === "saving"}
          onChange={(event) => setQuery(event.target.value)} />
      </label>
      <label className="admin-client-picker">
        <span>{props.t("staffConsole.adminCalls.links.client")}</span>
        <select value={selectedId} disabled={state === "saving" || !filtered.length}
          onChange={(event) => setSelectedId(event.target.value)}>
          {filtered.map((client) => <option key={client.id} value={client.id}>{client.name} · {client.email}</option>)}
        </select>
      </label>
      {query.trim() && !filtered.length && <p className="staff-empty">{props.t("staffConsole.adminCalls.links.noResults")}</p>}
      <div className="admin-call-action-feedback" aria-live="polite">
        {state === "loading" && <p>{props.t("common.loading")}</p>}
        {state === "saving" && <p>{props.t("staffConsole.adminCalls.links.creating")}</p>}
        {state === "ensured" && <p className="success-message">{props.t("staffConsole.adminCalls.links.created")}</p>}
        {state === "copied" && <p className="success-message">{props.t("staffConsole.adminCalls.links.copied")}</p>}
        {state === "load_error" && <p className="error-message" role="alert">{props.t("staffConsole.adminCalls.links.loadError")}</p>}
        {state === "save_error" && <p className="error-message" role="alert">{props.t("staffConsole.adminCalls.links.saveError")}</p>}
        {state === "copy_error" && <p className="error-message" role="alert">{props.t("staffConsole.adminCalls.links.copyError")}</p>}
      </div>
      {selected && links && state !== "loading" && (
        <AdminUnlockLinkCard t={props.t} client={selected} links={links} busy={state === "saving"}
          onCopy={(path) => void copyLink(path)} onEnsure={() => void ensureLinks()} />
      )}
      {!props.clients.length && <p className="staff-empty">{props.t("staffConsole.adminCalls.links.empty")}</p>}
    </section>
  );
}

export function AdminUnlockLinkCard(props: {
  t: (key: string) => string;
  client: AdminCallClient;
  links: AdminStableCallLinks;
  busy: boolean;
  onCopy: (path: string) => void;
  onEnsure: () => void;
}) {
  const entries = (["onboarding", "marketing"] as const).map((kind) => ({ kind, path: props.links[kind] }));
  const missing = entries.some((entry) => !entry.path);
  return (
    <article className="admin-unlock-link-card">
      <header><strong>{props.client.name}</strong><small>{props.client.email}</small></header>
      <div className="admin-unlock-link-list">
        {entries.map(({ kind, path }) => (
          <div key={kind}>
            <span>{props.t(`staffConsole.adminCalls.links.${kind}`)}</span>
            {path ? (
              <>
                <a href={path} target="_blank" rel="noreferrer">{path}</a>
                <button className="secondary-button" type="button" disabled={props.busy} onClick={() => props.onCopy(path)}>
                  {props.t("staffConsole.adminCalls.links.copy")}
                </button>
              </>
            ) : <em>{props.t("staffConsole.adminCalls.links.missing")}</em>}
          </div>
        ))}
      </div>
      {missing && (
        <button className="secondary-button" type="button" disabled={props.busy} onClick={props.onEnsure}>
          {props.t("staffConsole.adminCalls.links.create")}
        </button>
      )}
    </article>
  );
}

export function AdminCallConfirmation(props: CommonProps & {
  result: CreatedAdminCalls;
  onOpen: (id: string) => void;
}) {
  const [copyState, setCopyState] = useState<{ id: string; status: "copied" | "error" } | null>(null);
  async function copyZoomUrl(occurrenceId: string, zoomUrl: string) {
    try {
      await navigator.clipboard.writeText(zoomUrl);
      setCopyState({ id: occurrenceId, status: "copied" });
    } catch {
      setCopyState({ id: occurrenceId, status: "error" });
    }
  }
  return (
    <section className="admin-call-confirmation" role="status">
      <div>
        <p className="panel-label">{props.t("staffConsole.adminCalls.confirmation.kicker")}</p>
        <h4>{props.t(props.result.occurrences.length > 1
          ? "staffConsole.adminCalls.confirmation.seriesCreated"
          : "staffConsole.adminCalls.confirmation.created")}</h4>
        <p>{props.t("staffConsole.adminCalls.confirmation.codeOnce")}</p>
      </div>
      <div className="admin-call-created-list">
        {props.result.occurrences.map((occurrence) => (
          <article key={occurrence.occurrenceId}>
            <time dateTime={occurrence.startsAt}>{formatCallDate(occurrence.startsAt, props.locale)}</time>
            <a href={occurrence.zoomUrl} target="_blank" rel="noreferrer">{occurrence.zoomUrl}</a>
            <code>{occurrence.code}</code>
            <div className="admin-call-created-actions">
              <button className="secondary-button" type="button"
                onClick={() => void copyZoomUrl(occurrence.occurrenceId, occurrence.zoomUrl)}>
                {props.t(copyState?.id === occurrence.occurrenceId && copyState.status === "copied"
                  ? "staffConsole.adminCalls.actions.linkCopied"
                  : "staffConsole.adminCalls.actions.copyLink")}
              </button>
              <button className="text-button" type="button" onClick={() => props.onOpen(occurrence.occurrenceId)}>
                {props.t("staffConsole.adminCalls.actions.open")}
              </button>
              <AdminCallCopyStatus
                state={copyState?.id === occurrence.occurrenceId ? copyState.status : "idle"}
                t={props.t}
              />
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export function AdminCallActionFeedback(props: {
  state: "idle" | "busy" | "error" | "done";
  messageKey: string;
  t: (key: string) => string;
}) {
  return (
    <div className="admin-call-action-feedback" aria-live="polite">
      {props.state === "busy" && <p>{props.t("staffConsole.adminCalls.actionWorking")}</p>}
      {props.state === "done" && <p className="success-message">{props.t(props.messageKey)}</p>}
      {props.state === "error" && (
        <p className="error-message" role="alert">{props.t("staffConsole.adminCalls.error.action")}</p>
      )}
    </div>
  );
}

export function AdminCallCopyStatus(props: {
  state: "idle" | "copied" | "error";
  t: (key: string) => string;
}) {
  if (props.state !== "error") return null;
  return <p className="error-message admin-call-copy-status" role="alert">{props.t("staffConsole.adminCalls.error.copy")}</p>;
}

export function AdminCallRows(props: CommonProps & {
  calls: AdminCall[];
  selectedId: string | null;
  busy: boolean;
  onSelect: (id: string) => void;
  onCancel: (call: AdminCall) => void;
  onCancelFuture: (call: AdminCall) => void;
  onExtend: (call: AdminCall) => void;
  onRevokeCode: (call: AdminCall) => void;
  onRenewCode: (call: AdminCall) => void;
}) {
  return (
    <div className="admin-call-list">
      {props.calls.map((call) => {
        const selected = props.selectedId === call.id;
        const future = Date.parse(call.startsAt) >= Date.now();
        const noShowCount = future ? 0 : Math.max(0, call.enrolledCount - call.attendedCount);
        const codeState = adminCallCodeState(call, new Date().toISOString());
        const full = call.enrolledCount >= call.capacity;
        return (
          <article key={call.id} className={`admin-call-row${selected ? " selected" : ""}${call.cancelledAt ? " cancelled" : ""}`}>
            <button className="admin-call-row-main" type="button" aria-expanded={selected} onClick={() => props.onSelect(call.id)}>
              <span className={`call-state-dot ${call.cancelledAt ? "cancelled" : future ? "upcoming" : "past"}`} aria-hidden="true" />
              <span className="admin-call-row-date">
                <strong>{formatCallDate(call.startsAt, props.locale)}</strong>
                <small>{props.t(`staffConsole.callKindValue.${call.kind}`)} · {call.locale.toUpperCase()}</small>
              </span>
              <span className="admin-call-row-fact">
                <small>{props.t("staffConsole.adminCalls.reservation")}</small>
                <strong>{`${call.enrolledCount} / ${call.capacity}`}</strong>
              </span>
              <span className="admin-call-row-fact">
                <small>{props.t("staffConsole.adminCalls.code.label")}</small>
                <strong>{props.t(`staffConsole.adminCalls.code.${codeState}`)}</strong>
              </span>
              <span className="admin-call-row-fact series-fact">
                <small>{props.t("staffConsole.adminCalls.series.label")}</small>
                <strong>{props.t(call.seriesId ? "staffConsole.adminCalls.series.weekly" : "staffConsole.adminCalls.series.single")}</strong>
              </span>
              {call.cancelledAt ? (
                <span className="status-chip off">{props.t("staffConsole.adminCalls.cancelled")}</span>
              ) : full ? (
                <span className="status-chip off">{props.t("staffConsole.adminCalls.full")}</span>
              ) : null}
            </button>
            {selected && (
              <div className="admin-call-detail">
                <dl>
                  <div><dt>{props.t("staffConsole.zoomUrl")}</dt><dd><a href={call.zoomUrl} target="_blank" rel="noreferrer">{call.zoomUrl}</a></dd></div>
                  {call.recordingUrl && (
                    <div><dt>{props.t("staffConsole.recordingUrl")}</dt><dd><a href={call.recordingUrl} target="_blank" rel="noreferrer">{call.recordingUrl}</a></dd></div>
                  )}
                  <div><dt>{props.t("staffConsole.adminCalls.reservation")}</dt><dd>{`${call.enrolledCount} / ${call.capacity}`}</dd></div>
                  <div><dt>{props.t("staffConsole.adminCalls.attended")}</dt><dd>{call.attendedCount}</dd></div>
                  <div><dt>{props.t("staffConsole.adminCalls.watched")}</dt><dd>{call.watchedCount}</dd></div>
                  <div><dt>{props.t("staffConsole.adminCalls.noShow")}</dt><dd>{noShowCount}</dd></div>
                  <div><dt>{props.t("staffConsole.adminCalls.code.used")}</dt><dd>{call.codeRedemptionCount}</dd></div>
                  {call.codeLastRedeemedAt && (
                    <div><dt>{props.t("staffConsole.adminCalls.code.lastUsed")}</dt><dd>{formatCallDate(call.codeLastRedeemedAt, props.locale)}</dd></div>
                  )}
                  {call.intervalWeeks && <div><dt>{props.t("staffConsole.adminCalls.repeat.interval")}</dt><dd>{call.intervalWeeks}</dd></div>}
                </dl>
                {!future && <p className="admin-call-metrics-hint">{props.t("staffConsole.adminCalls.metricsHint")}</p>}
                {future && !call.cancelledAt && (
                  <div className="admin-call-detail-actions">
                    <button className="danger-button" type="button" disabled={props.busy} onClick={() => props.onCancel(call)}>
                      {props.t("staffConsole.adminCalls.actions.cancelOne")}
                    </button>
                    {call.codeId && call.codeActive === 1 && (
                      <button className="secondary-button" type="button" disabled={props.busy} onClick={() => props.onRevokeCode(call)}>
                        {props.t("staffConsole.adminCalls.actions.revokeCode")}
                      </button>
                    )}
                    <button className="secondary-button" type="button" disabled={props.busy} onClick={() => props.onRenewCode(call)}>
                      {props.t(call.codeId ? "staffConsole.adminCalls.actions.renewCode" : "staffConsole.adminCalls.actions.createCode")}
                    </button>
                    {call.seriesId && (
                      <>
                        <button className="secondary-button" type="button" disabled={props.busy || (call.seriesOccurrenceCount ?? 0) >= 104}
                          onClick={() => props.onExtend(call)}>
                          {props.t("staffConsole.adminCalls.actions.extend")}
                        </button>
                        <button className="text-button" type="button" disabled={props.busy} onClick={() => props.onCancelFuture(call)}>
                          {props.t("staffConsole.adminCalls.actions.cancelFuture")}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}

function AdminDateTimeField(props: {
  value: AdminDateTimeParts;
  onChange: (value: AdminDateTimeParts) => void;
  t: (key: string) => string;
}) {
  const currentYear = new Date().getFullYear();
  const field = (key: keyof AdminDateTimeParts, values: string[]) => (
    <label>
      <span>{props.t(`staffConsole.adminCalls.dateTime.${key}`)}</span>
      <select value={props.value[key]} required
        onChange={(event) => props.onChange({ ...props.value, [key]: event.target.value })}>
        <option value="">{props.t("staffConsole.adminCalls.dateTime.choose")}</option>
        {values.map((value) => <option key={value} value={value}>{value}</option>)}
      </select>
    </label>
  );
  return (
    <fieldset className="admin-date-time-field">
      <legend>{props.t("staffConsole.callStartsAt")}</legend>
      <div>
        {field("day", range(1, 31))}
        {field("month", range(1, 12))}
        {field("year", range(currentYear, currentYear + 2, false))}
        {field("hour", range(0, 23))}
        {field("minute", range(0, 59))}
      </div>
    </fieldset>
  );
}

function range(start: number, end: number, pad = true): string[] {
  const values: string[] = [];
  for (let value = start; value <= end; value += 1) values.push(pad ? String(value).padStart(2, "0") : String(value));
  return values;
}

function normalizeCall(call: AdminCall): AdminCall {
  return {
    ...call,
    capacity: Number(call.capacity),
    intervalWeeks: call.intervalWeeks === null ? null : Number(call.intervalWeeks),
    seriesOccurrenceCount: call.seriesOccurrenceCount === null ? null : Number(call.seriesOccurrenceCount),
    enrolledCount: Number(call.enrolledCount),
    attendedCount: Number(call.attendedCount),
    watchedCount: Number(call.watchedCount),
    codeRedemptionCount: Number(call.codeRedemptionCount),
    codeActive: call.codeActive === null ? null : Number(call.codeActive)
  };
}

export function selectAdminCallClientId(
  clients: AdminCallClient[],
  query: string,
  currentId: string
): string {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const matches = clients.filter((client) =>
    `${client.name} ${client.email}`.toLocaleLowerCase().includes(normalizedQuery)
  );
  return matches.some((client) => client.id === currentId) ? currentId : matches[0]?.id ?? "";
}

export function adminCallCodeState(
  call: Pick<AdminCall, "codeId" | "codeActive" | "codeNotBefore" | "codeExpiresAt">,
  now: string
): "missing" | "inactive" | "scheduled" | "ready" | "expired" {
  if (!call.codeId) return "missing";
  if (call.codeActive !== 1) return "inactive";
  if (call.codeNotBefore && call.codeNotBefore > now) return "scheduled";
  if (call.codeExpiresAt && call.codeExpiresAt <= now) return "expired";
  return "ready";
}

function formatCallDate(value: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLanguage(locale), {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    ...clockFields(locale)
  }).format(new Date(value));
}

function toLocalDateTimeInput(value: Date): string {
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
}
